import { Bones } from '@simplywallst/ui-core';
import { useIndustryMetrics } from '@/hooks/useIndustryMetrics';
import type { PrimaryCountryISO } from '@/constants/countries';
import { cn, Link } from '@simplywallst/ui-core/next';
import { useQuery } from '@tanstack/react-query';
import type { MarketSummary } from '@/pages/Market/query/fetchMarketSummary';
import {
  fetchMarketSummary,
  getMarketSummaryQueryKey,
} from '@/pages/Market/query/fetchMarketSummary';
import type { getMarketSummary } from '@simplywallst/services';

type MarketSummaryTemplate = Parameters<typeof getMarketSummary>[0];
const defaultSummary: MarketSummary = {
  text: 'No data',
  sentiment: null,
  virtual: [],
};

function useMarketSummary(
  template: MarketSummaryTemplate,
  market: PrimaryCountryISO
): {
  summary: MarketSummary;
  isFetching: boolean;
} {
  const { data, isFetching } = useQuery({
    queryKey: getMarketSummaryQueryKey(market, 0),
    queryFn: fetchMarketSummary,
    staleTime: Infinity,
    refetchOnMount: false,
  });

  const summary = (data && data[template]) || defaultSummary;
  return { summary, isFetching };
}

const PNV = ({
  value,
  testId,
  children,
}: {
  value: number | null | undefined;
  testId?: string;
  children: React.ReactNode;
}) => (
  <span
    className={cn(
      'text-sm font-medium',
      value === 0 || value === null || value === undefined
        ? 'text-softer'
        : value > 0
          ? 'text-good'
          : 'text-bad'
    )}
    data-cy-id={testId}
  >
    {children}
  </span>
);

export const MarketPerformance = ({
  market,
  industryId,
}: {
  market: PrimaryCountryISO;
  industryId: number;
}) => {
  const { summary, isFetching } = useMarketSummary('market-overview', market);
  const { averages: industryMetricsAverages } = useIndustryMetrics(
    market,
    industryId
  );

  return (
    <>
      <ul className="flex gap-6 text-tiny text-softer [&>li]:flex [&>li]:flex-col">
        <li>
          <span aria-hidden="true">7D</span>
          <span className="sr-only">7 Days: </span>
          <PNV
            value={industryMetricsAverages.return7D}
            testId="market-performance-7d"
          >
            {industryMetricsAverages.return7DFormatted}
          </PNV>
        </li>
        <li>
          <span aria-hidden="true">3M</span>
          <span className="sr-only">3 Months: </span>
          <PNV
            value={industryMetricsAverages.return90D}
            testId="market-performance-3m"
          >
            {industryMetricsAverages.return90DFormatted}
          </PNV>
        </li>
        <li>
          <span aria-hidden="true">1Y</span>
          <span className="sr-only">1 Year: </span>
          <PNV
            value={industryMetricsAverages.return1YrAbs}
            testId="market-performance-1y"
          >
            {industryMetricsAverages.return1YrAbsFormatted}
          </PNV>
        </li>
        <li>
          <span aria-hidden="true">YTD</span>
          <span className="sr-only">Year to Date: </span>
          <PNV
            value={industryMetricsAverages.returnYTD}
            testId="market-performance-ytd"
          >
            {industryMetricsAverages.returnYTDFormatted}
          </PNV>
        </li>
      </ul>
      {!isFetching ? (
        <div className="max-w-prose text-pretty pr-2 text-xs">
          {summary.text}{' '}
          <Link
            href={`/markets/${market.toLowerCase()}`}
            className="whitespace-nowrap text-softer no-underline"
          >
            Market details &rsaquo;
          </Link>
        </div>
      ) : (
        // 5x Bones is roughly the height of MarketBrief
        <>
          <Bones maxWidth="100%" />
          <Bones maxWidth="100%" />
          <Bones maxWidth="100%" />
          <Bones maxWidth="100%" />
          <Bones maxWidth="100%" />
        </>
      )}
    </>
  );
};
